import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLoadedAudioId } from "../../domains/app/contexts/audio.context";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../../components/Loader";
import PlaylistCard from "../../components/cards/PlaylistCard";
import { RoomHeader } from "../../components/room/RoomHeader";
import { Tabs } from "../../components/ui/Tabs";
import { PublicationCard } from "../../components/ui/PublicationCard";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { SectionLink } from "../../components/ui/SectionLink";
import { Badge } from "../../components/ui/Badge";
import PlaylistPanel from "../../components/app/PlaylistPanel";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import { useSaveArticle } from "../../domains/user/hooks/useSaveArticle";
import { useShareArticle } from "../../domains/user/hooks/useShareArticle";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../domains/app/utils/getArticleTypeText";
import { Button } from "../../components/ui/Button";
import {
  hasReadArticle,
  isRecentArticle,
} from "../../domains/article/utils/articleReads";
import { IconAdjustmentsHorizontal, IconPlus } from "@tabler/icons-react";
import TopicFilterPanel from "../../components/filters/TopicFilterPanel";
import DownloadAppModal from "../../components/modals/DownloadAppModal";
import { useIsRoomWelcomeOpen } from "../../components/room/RoomWelcomeHost";

import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import RoomEventDisplay from "../../components/room/RoomEventDisplay";
import { z } from "zod";
import { useShareRoom } from "../../domains/room/hooks/useShareRoom";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { RoomListWrapper } from "../../components/room/layout";
import { gaEventSelectItemArticle } from "../../tools/analytics/domains/article.analytics";
import { getRoomColors } from "../../domains/room/utils/getRoomColors";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import {
  getStoredContentFilters,
  storeContentFilters,
} from "../../domains/app/utils/contentFilters";
import { gaEventSetTopicFilter } from "../../tools/analytics/domains/filter.analytics";
import {
  clearScrollPosition,
  getScrollPosition,
  setScrollPosition,
} from "../../domains/app/stores/scrollPositionsStore";
import { recordRoomVisit } from "../../domains/room/utils/roomVisits";
import { useGetRecommendedPlaylistsQuery } from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { EventDisplayDestination } from "../../../../api/src/shared/domain/room";
import {
  AnalyticsItemList,
  AnalyticsNavElementName,
} from "../../tools/analytics/analytics.types";
import { navClickBottomBar } from "../../tools/analytics/domains/nav.analytics";
import {
  gaEventSelectItemPlaylist,
  gaEventViewItemListPlaylist,
} from "../../tools/analytics/domains/playlist.analytics";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { roomDiscoveryChannelSchema } from "../../tools/analytics/domains/invite.analytics";
import RoomDisclaimerBanner from "../../components/room/RoomDisclaimerBanner";
import RoomExplainerBottomSheet from "../../components/room/RoomExplainerBottomSheet";
import { RoomCongressHero } from "../../components/room/RoomCongressHero";
import { partnerAccentColor } from "../../domains/congress/partnerAccentColor";
import { useArticleSwipe } from "../../features/article/hooks/useArticleSwipe";

import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { RoomOAuthGate } from "../../components/room/RoomOAuthGate";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useTranslation } from "react-i18next";
import { useRoomInfoHeader } from "../../domains/room/hooks/useRoomInfoHeader";
import { useJoinRoom } from "../../domains/room/hooks/useJoinRoom";
import { useCanShowJoin } from "../../domains/room/hooks/useCanShowJoin";
import { useApiInfiniteQuery, useApiQuery, type Api } from "../../services/api";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { authorLine } from "../../domains/article/utils/authorLine";
import { formatArticleDate } from "../../domains/app/utils/renderDate";
const ANALYTICS_LIST_NAME = AnalyticsItemList.ROOM_DISCOVERY;

export default function RoomDiscoveryPage({
  roomId,
  room,
}: {
  roomId: string;
  room: PrivateRoom;
}) {
  const history = useHistory();
  const isRoomWelcomeOpen = useIsRoomWelcomeOpen();
  const { publicPlaylistLink, articleLink } = usePageLinks(roomId);
  const location = useLocation();
  const {
    roomDiscoveryChannel,
    roomsSearch,
    backTo: backToOverride,
  } = useLocationState(
    z.object({
      roomDiscoveryChannel: roomDiscoveryChannelSchema.default("public app"),
      roomsSearch: z.string().default(""),
      // Set by the caller (e.g. the Discovery feed) to send "back" to the
      // exact page they came from instead of the room center fallback.
      backTo: z.string().nullable().default(null),
    })
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  const roomQuery = useApiQuery("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const { roomTextColor } = getRoomColors(currentRoom?.primaryColor ?? null);
  const scrollPathname = globalThis.location.pathname;
  // Gradual migration: a room that has curated `filterTopics` uses the new
  // topic filter; a room that has not been migrated yet falls back to the old
  // specialty/tag filter, so it keeps working unchanged until topics are set.
  const usesTopicFilter =
    currentRoom !== null && currentRoom.filterTopicModels.length > 0;
  // Seed from storage on the first render (not just in the roomId effect below):
  // the effect runs AFTER the initial articles request, so an empty seed would
  // load the unfiltered feed for a beat before snapping to the saved filter.
  // getStoredContentFilters is keyed by pathname, which is the room's path here.
  const [topicFilters, setTopicFilters] = useState<string[]>(
    () => getStoredContentFilters().topicUids
  );
  const [topicMatch, setTopicMatch] = useState<"any" | "all">(
    () => getStoredContentFilters().topicMatch
  );
  // Tag fallback selection (un-migrated rooms). Reuses the store's tag key
  // (`subSpecialtyIds`) so it stays compatible with the other pages that share
  // this store; `tagMatch` is the union/intersection choice for that mode.
  const [tagFilters, setTagFilters] = useState<string[]>(
    () => getStoredContentFilters().subSpecialtyIds
  );
  const [tagMatch, setTagMatch] = useState<"any" | "all">(
    () => getStoredContentFilters().tagMatch
  );

  // One filter panel, two data sources: a migrated room offers its curated
  // topics (keyed by uid); an un-migrated room offers its tags (keyed by id).
  // Both are presented identically as "topics".
  const filterItems =
    currentRoom === null
      ? []
      : usesTopicFilter
        ? currentRoom.filterTopicModels.map((topic) => ({
            key: topic.uid,
            translations: topic.translations,
          }))
        : currentRoom.tagModels.map((tag) => ({
            key: tag.id,
            translations: tag.translations,
          }));
  const activeFilterKeys = usesTopicFilter ? topicFilters : tagFilters;
  const activeFilterMatch = usesTopicFilter ? topicMatch : tagMatch;

  // Apply the panel's result to the right mode: persist to the store, mirror
  // into local state (drives the feed query), and log analytics once.
  function applyFilters(keys: string[], match: "any" | "all"): void {
    if (usesTopicFilter) {
      setTopicFilters(keys);
      setTopicMatch(match);
      storeContentFilters({ topicUids: keys, topicMatch: match });
    } else {
      setTagFilters(keys);
      setTagMatch(match);
      storeContentFilters({ subSpecialtyIds: keys, tagMatch: match });
    }
    if (keys.length > 0) {
      gaEventSetTopicFilter({
        keys,
        matchMode: match,
        roomName: room?.translations.en.name,
      });
    }
  }

  // Redirect to playlist if query param is present (from QR code invite)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const playlistId = params.get("playlist");
    if (playlistId !== null) {
      history.replace(`/room/playlist/${roomId}/${playlistId}`);
    }
  }, [location.search, roomId, history]);

  // Record visit for new-content badge tracking
  useEffect(() => {
    if (roomId) recordRoomVisit(roomId);
  }, [roomId]);

  // Reset filters when switching rooms
  useEffect(() => {
    const stored = getStoredContentFilters();
    setTopicFilters(stored.topicUids);
    setTopicMatch(stored.topicMatch);
    setTagFilters(stored.subSpecialtyIds);
    setTagMatch(stored.tagMatch);
  }, [roomId]);
  // A tag-filtered room preserves the old behavior of also scoping to its main
  // specialty (the sub-specialty panel used to send both); derive it from the
  // room rather than storing it separately.
  const tagFilterSpecialty =
    !usesTopicFilter &&
    tagFilters.length > 0 &&
    currentRoom !== null &&
    currentRoom.mainSpecialtyModel !== null
      ? currentRoom.mainSpecialtyModel.id
      : undefined;
  const articleListQuery = useApiInfiniteQuery("/webapp/public/articles", {
    query: {
      limit: 20,
      sort: "default",
      language: contentLang,
      roomId,
      // Exactly one filter mode is active per room: a migrated room sends
      // topics + topicsMatch; an un-migrated room sends tags + tagsMatch (and
      // the main-specialty scope the old tag filter carried).
      topics:
        usesTopicFilter && topicFilters.length > 0
          ? topicFilters.join(",")
          : undefined,
      topicsMatch:
        usesTopicFilter && topicFilters.length > 0 ? topicMatch : undefined,
      tags:
        !usesTopicFilter && tagFilters.length > 0
          ? tagFilters.join(",")
          : undefined,
      tagsMatch:
        !usesTopicFilter && tagFilters.length > 0 ? tagMatch : undefined,
      medical_specialties: tagFilterSpecialty,
    },
  });
  const displayedContent =
    articleListQuery.state === "success" ? articleListQuery.docs : [];
  const {
    setSwipeList: setArticleSwipeList,
    setSwipeIndex: setArticleSwipeIndex,
  } = useArticleSwipe();
  const isLoadingContent = articleListQuery.state === "pending";
  const hadCachedArticles = useRef(!isLoadingContent);
  const playlistCache = useGetRecommendedPlaylistsQuery(roomId);
  const displayedPlaylists = playlistCache.playlists.slice(0, 5);
  const displayedPlaylistsIds = playlistCache.playlistIds;
  const { triggerShare, renderShareSheet } = useShareRoom(roomId, room);
  const [isFilterPanelVisible, setFilterPanelVisible] = useState(false);
  // The filter shows when the admin enabled it AND the room has something to
  // filter by (curated topics, or tags for the fallback). Empty on both → hide.
  const isFiltersAvailable =
    currentRoom !== null &&
    (currentRoom.discoveryFilter ?? false) &&
    filterItems.length > 0;
  // Whether any filter is currently applied (drives the funnel highlight).
  const hasActiveFilter = activeFilterKeys.length > 0;
  const { currentUser } = useCurrentUser();
  const canShowJoin = useCanShowJoin(roomId, room);
  const [isExplainerOpen, setExplainerOpen] = useState(false);
  const { joinRoom, isJoining } = useJoinRoom(
    roomId,
    roomDiscoveryChannel,
    "greet"
  );
  const joinButton = canShowJoin ? (
    <Button
      variant="primary"
      className="w-full"
      disabled={isJoining}
      leftIcon={isJoining ? null : <IconPlus />}
      onClick={joinRoom}
    >
      {isJoining ? <Loader /> : t("Join")}
    </Button>
  ) : null;
  const {
    roomInfoRef,
    hasScrolledPastInfo,
    handleScroll: handleRoomInfoScroll,
    RoomInfoSection,
  } = useRoomInfoHeader(roomId, {
    footer: joinButton,
    onInfoClick: () => {
      setExplainerOpen(true);
    },
  });
  const lastScrollYRef = useRef(0);
  const [_showFloatingJoin, setShowFloatingJoin] = useState(false); // TODO: use/delete it when behavior has been defined

  function handleScroll() {
    handleRoomInfoScroll();
    const currentY = scrollRef.current ? scrollRef.current.scrollTop : 0;
    const scrollingUp = currentY < lastScrollYRef.current;
    lastScrollYRef.current = currentY;
    if (currentY > 0) {
      setScrollPosition(scrollPathname, currentY);
    }

    const infoPastView = roomInfoRef.current
      ? roomInfoRef.current.getBoundingClientRect().bottom < 0
      : false;

    setShowFloatingJoin(scrollingUp && infoPastView);
  }

  // Reset scroll to top when switching to a different room (forward navigation).
  useLayoutEffect(() => {
    if (history.action === "POP") return;
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: 0, behavior: "instant" });
  }, [roomId]);

  // Restore scroll from sessionStorage on back navigation.
  // Depends on both currentRoom (so ScrollableLayout is mounted) and content
  // being loaded (so the container is tall enough to scroll to the saved offset).
  const hasArticles = displayedContent.length > 0;
  useLayoutEffect(() => {
    if (history.action !== "POP") return;
    if (!scrollRef.current) return;
    if (!hasArticles) return;
    const saved = getScrollPosition(scrollPathname);
    if (saved > 0) {
      scrollRef.current.scrollTop = saved;
      clearScrollPosition(scrollPathname);
    }
  }, [currentRoom, hasArticles]);

  // Reset scroll when filters change
  useEffect(() => {
    if (
      !isLoadingContent &&
      displayedContent.length === 0 &&
      scrollRef.current
    ) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [topicFilters, topicMatch, tagFilters, tagMatch]);

  useEffectOnce(() => {
    if (displayedPlaylists.length > 0) {
      gaEventViewItemListPlaylist({
        playlists: displayedPlaylists,
        listName: ANALYTICS_LIST_NAME,
      });
    }
  }, [displayedPlaylistsIds]);

  function handleScrollEnd() {
    if (
      articleListQuery.state === "success" &&
      articleListQuery.canFetchNextPage
    )
      articleListQuery.fetchNextPage();
  }

  if (!currentRoom) return <Loader expanded />;

  const isInitialPage = history.length <= 2;
  const headerBackTo =
    backToOverride !== null
      ? backToOverride
      : isInitialPage
        ? "/discovery"
        : `/rooms${roomsSearch}`;

  const renderHeader = () => (
    <RoomHeader
      roomId={roomId}
      backTo={headerBackTo}
      desktopBackTo={null}
      backLabel={backToOverride === null && isInitialPage ? t("Home") : null}
      showJoinButton={hasScrolledPastInfo && canShowJoin}
      currentUser={currentUser}
      onJoinClick={joinRoom}
      onShareClick={() => {
        void triggerShare();
      }}
    />
  );

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={renderHeader()}
      footer={null}
      style={{ paddingTop: 0, overscrollBehavior: "none" }}
      onScroll={handleScroll}
      onScrollEnd={handleScrollEnd}
    >
      <div className="bg-white pb-12">
        {RoomInfoSection}

        {(currentRoom.congressTab === true ||
          currentRoom.resourcesTab === true) && (
          <Tabs
            className="sticky top-[calc(48px+var(--safe-area-top))] z-30"
            tabs={[
              {
                value: "discovery",
                label: t("Discovery"),
                badge: null,
                icon: null,
              },
              {
                value: "resources",
                label: t("Resources"),
                badge: null,
                icon: null,
                activeStyle: {
                  color: currentRoom.primaryColor ?? undefined,
                  backgroundColor:
                    currentRoom.primaryColor !== null
                      ? `${currentRoom.primaryColor}0F`
                      : undefined,
                },
                inactiveStyle: {
                  color: currentRoom.primaryColor ?? undefined,
                  backgroundColor:
                    currentRoom.primaryColor !== null
                      ? `${currentRoom.primaryColor}08`
                      : undefined,
                },
                indicatorStyle:
                  currentRoom.primaryColor !== null
                    ? { backgroundColor: currentRoom.primaryColor }
                    : undefined,
              },
            ]}
            activeTab="discovery"
            onTabChange={(tab) => {
              if (tab === "resources") {
                navClickBottomBar(AnalyticsNavElementName.RESOURCES, "room");
                history.push(`/room/resources/${roomId}`, {
                  roomDiscoveryChannel,
                  roomsSearch,
                });
              }
            }}
          />
        )}

        <RoomDisclaimerBanner roomId={roomId} />

        <RoomCongressHero
          roomId={roomId}
          organisationName={currentRoom.organisationModel.name}
          accentColor={partnerAccentColor(currentRoom)}
          frame="padded"
        />

        {/* Thematic Collections carousel — hidden on lg+ when right rail is visible */}
        {(playlistCache.isLoading || displayedPlaylists.length > 0) && (
          <div className="@container bg-white py-4 text-primary-black">
            <div className="flex items-center justify-between px-5 py-2">
              <SectionTitle title={t("Thematic Collections")} />
              {displayedPlaylists.length >= 2 && (
                <SectionLink to={`/room/playlists/${roomId}`}>
                  {t("See all")}
                </SectionLink>
              )}
            </div>
            <div className="scrollbar-hidden flex gap-3 overflow-x-auto py-2 snap-x snap-mandatory scroll-pl-5 md:grid md:grid-cols-2 md:overflow-visible md:snap-none md:px-5">
              <div className="shrink-0 w-[11px] md:hidden" aria-hidden="true" />
              {playlistCache.isLoading
                ? [0, 1].map((i) => (
                    <div
                      key={i}
                      className="min-w-0 shrink-0 basis-[85%] snap-start md:basis-auto"
                    >
                      <PlaylistCard
                        loading
                        variant="in-room"
                        primaryColor={room.primaryColor}
                        secondaryColor={room.secondaryColor}
                      />
                    </div>
                  ))
                : displayedPlaylists.map((playlist) => (
                    <div
                      key={playlist.id}
                      className="min-w-0 shrink-0 basis-[85%] snap-start md:basis-auto"
                    >
                      <PlaylistCard
                        playlist={playlist}
                        variant="in-room"
                        primaryColor={room.primaryColor}
                        secondaryColor={room.secondaryColor}
                        onClick={() => {
                          gaEventSelectItemPlaylist({
                            playlist,
                            listName: ANALYTICS_LIST_NAME,
                          });
                          history.push({
                            pathname: publicPlaylistLink(playlist),
                            state: { playlist },
                          });
                        }}
                      />
                    </div>
                  ))}
              <div className="shrink-0 w-[11px] md:hidden" aria-hidden="true" />
            </div>
          </div>
        )}

        <RoomEventDisplay
          roomId={roomId}
          roomTextColor={roomTextColor ?? null}
          pageType={EventDisplayDestination.DISCOVERY}
        />

        <div className="flex items-center justify-between gap-3 border-t border-ds-slate-100 px-4 pb-3 pt-6">
          <SectionTitle title={t("Latest News")} />
          {isFiltersAvailable && (
            <button
              type="button"
              aria-label={t("Filters")}
              onClick={() => {
                setFilterPanelVisible(true);
              }}
              className="inline-flex size-6 cursor-pointer items-center justify-center text-ds-black"
            >
              <IconAdjustmentsHorizontal
                size={20}
                stroke={2}
                className={hasActiveFilter ? "text-ds-mint-700" : undefined}
              />
            </button>
          )}
        </div>

        {isLoadingContent ? (
          <RoomListWrapper key="loading" className="px-4">
            {Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className="animate-fade-in-up"
                style={{
                  animationDelay: `${Math.round(60 * Math.sqrt(index))}ms`,
                  animationFillMode: "both",
                }}
              >
                <SkeletonPublicationCard showImage showDescription />
              </div>
            ))}
          </RoomListWrapper>
        ) : (
          <div className="flex w-full flex-col divide-y divide-ds-slate-100 px-4 *:py-4">
            {displayedContent.length === 0 && (
              <span className="no-results text-center font-dm-sans text-base text-primary-black">
                {t("No content available yet for this room")}
              </span>
            )}
            {displayedContent.map((content, index) => {
              const isNew =
                isRecentArticle(content.meta.creationDate) &&
                !hasReadArticle(content.id);
              return (
                <div
                  key={content.id}
                  className={
                    hadCachedArticles.current ? undefined : "animate-fade-in-up"
                  }
                  style={
                    hadCachedArticles.current
                      ? undefined
                      : {
                          animationDelay: `${Math.round(60 * Math.sqrt(index))}ms`,
                          animationFillMode: "both",
                        }
                  }
                >
                  <Badge
                    show={isNew}
                    label={t("New")}
                    position="tr"
                    className="z-10"
                    block
                  >
                    <LatestNewsRow
                      article={content}
                      onClick={() => {
                        setArticleSwipeList(displayedContent);
                        setArticleSwipeIndex(index);
                        gaEventSelectItemArticle({
                          article: content,
                          listName: ANALYTICS_LIST_NAME,
                        });
                        history.push(articleLink({ slug: content.slug }));
                      }}
                    />
                  </Badge>
                </div>
              );
            })}
            {articleListQuery.state === "success" &&
              articleListQuery.isFetching &&
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="animate-fade-in-up"
                  style={{
                    animationDelay: `${Math.round(60 * Math.sqrt(index))}ms`,
                    animationFillMode: "both",
                  }}
                >
                  <SkeletonPublicationCard showImage showDescription />
                </div>
              ))}
          </div>
        )}
      </div>

      {renderShareSheet()}

      <RoomExplainerBottomSheet
        roomId={roomId}
        presentation="sheet"
        isOpen={isExplainerOpen}
        onClose={() => {
          setExplainerOpen(false);
        }}
      />

      {/* Held back while the welcome sheet is on screen: it would stack on
          top of it. The prompt is a one-shot keyed on
          `firstVisitFlagsStore`, so holding it back does not burn it; it
          fires on the next room visit. */}
      {!isRoomWelcomeOpen && <DownloadAppModal />}

      <RoomOAuthGate roomId={roomId} />

      <TopicFilterPanel
        items={filterItems}
        selectedKeys={activeFilterKeys}
        match={activeFilterMatch}
        isOpen={isFilterPanelVisible}
        onClose={() => {
          setFilterPanelVisible(false);
        }}
        onApply={applyFilters}
      />
    </ScrollableLayout>
  );
}

type DiscoveryArticle =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

function LatestNewsRow({
  article,
  onClick,
}: {
  article: DiscoveryArticle;
  onClick: () => void;
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const playlistPanel = usePlaylistPanelContent();
  const { isSaved, toggleSaveToPlaylists } = useSaveArticle(
    article,
    ANALYTICS_LIST_NAME
  );
  const { triggerShare } = useShareArticle(ANALYTICS_LIST_NAME);

  const journal = article.journal;
  const author = authorLine(article.primary_author, article.authors);
  const publicationDate = formatArticleDate(article.publication_date, "en-US");
  const meta = getArticleTypeText(article.article_type);
  const cardImage = article.cardImage;
  const imageUrl =
    cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;

  return (
    <>
      <PublicationCard
        isInPlayer={loadedAudioId === article.id}
        title={article.title}
        journalName={journal === null ? null : journal.name}
        author={author}
        date={publicationDate}
        meta={meta}
        metaVariant={getArticleTypeVariant(article.article_type)}
        access={article.isOpenAccess === true ? "open" : "closed"}
        imageUrl={imageUrl}
        description={null}
        recommendation={null}
        isNew={false}
        isBookmarked={isSaved}
        onBookmark={() => {
          void playlistPanel.openArticle(article.slug);
        }}
        onShare={
          currentUser === null
            ? null
            : () => {
                void triggerShare(article, currentUser);
              }
        }
        onClick={onClick}
        onJournalClick={
          journal === null
            ? null
            : () => {
                history.push(`/journal/${journal.uid}`);
              }
        }
        onRecommendationClick={null}
        className="bg-transparent"
      />
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(article, { add, remove });
          }}
        />
      )}
    </>
  );
}
